import React, { useState } from 'react';
import { useSchool } from '../context/SchoolContext';
import { Plus, Search, FileText, Printer, X, Pencil } from 'lucide-react';
import logo from '../assets/logo.png';
import ImportModal from '../components/ImportModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import './Financials.css';

const Financials = () => {
    const { financials, customers, packages, getInstallmentName, importPayments, updatePayment, loadMorePayments, hasMorePayments } = useSchool();
    const [searchTerm, setSearchTerm] = useState('');
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isPaymentImportModalOpen, setIsPaymentImportModalOpen] = useState(false);
    const [paymentToEdit, setPaymentToEdit] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const handleEditPayment = (payment) => {
        setPaymentToEdit(payment);
        setIsPaymentModalOpen(true);
    };

    const handleAddPayment = () => {
        setPaymentToEdit(null);
        setIsPaymentModalOpen(true);
    };

    // CSV Template Details
    const paymentTemplateHeaders = ['Date', 'Amount', 'Student', 'Package', 'Payment Method', 'Description'];
    const paymentSampleRow = ['2026-02-01', '330', 'Elizabeth Gavaza', 'Full Package', 'Cash', 'Driving full package lessons'];

    const filteredTransactions = (financials.payments || []).filter(p => {
        const customer = customers.find(c => c.id === p.customerId);
        const name = (customer?.name || 'unknown').toLowerCase();
        return name.includes(searchTerm.toLowerCase()) || (p.id && p.id.includes(searchTerm));
    });

    const openInvoice = (payment) => {
        const customer = customers.find(c => c.id === payment.customerId);
        setSelectedInvoice({ ...payment, customerName: customer?.name || 'Unknown' });
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Financials</h2>
                    <p className="page-subtitle">Track income, invoices and receipts</p>
                </div>
                <div className="header-actions">
                    <button className="btn btn-secondary flex items-center gap-2" onClick={() => setIsPaymentImportModalOpen(true)}>
                        <FileText size={18} /> Import CSV
                    </button>
                    <button className="btn btn-primary flex items-center gap-2" onClick={handleAddPayment}>
                        <Plus size={18} /> Record Payment
                    </button>
                </div>
            </div>

            {/* Transactions / Invoices View */}
            <>
                <div className="filters-bar">
                    <div className="search-input-wrapper">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search: Customer Name or Ref ID..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="card table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Ref ID</th>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Installment</th>
                                <th>Method</th>
                                <th>Amount</th>
                                <th>Document</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTransactions.map(t => {
                                const customer = customers.find(c => c.id === t.customerId);
                                return (
                                    <tr key={t.id || Math.random()}>
                                        <td className="font-mono text-sm">{t.id || 'N/A'}</td>
                                        <td>{t.payment_date || t.date ? new Date(t.payment_date || t.date).toLocaleString() : 'N/A'}</td>
                                        <td className="font-medium">{customer ? customer.name : 'Unknown'}</td>
                                        <td>{getInstallmentName(t.installmentType)}</td>
                                        <td>{t.method}</td>
                                        <td className="font-bold text-green">${t.amount}</td>
                                        <td>
                                            <div className="flex gap-2">
                                                <button className="btn-link flex items-center gap-1" onClick={() => openInvoice(t)}>
                                                    <FileText size={14} /> View Receipt
                                                </button>
                                                <button className="icon-btn text-gold" onClick={() => handleEditPayment(t)} title="Edit">
                                                    <Pencil size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {hasMorePayments && (
                        <div className="flex justify-center p-4 border-t border-slate-100">
                            <button className="btn btn-secondary" onClick={loadMorePayments}>
                                Load More
                            </button>
                        </div>
                    )}
                </div>
            </>

            {/* Invoice/Receipt Modal */}
            {selectedInvoice && (() => {
                const customer = customers.find(c => c.id === selectedInvoice.customerId) || {};
                const pkg = packages.find(p => p.id === (customer.packageId || selectedInvoice.packageId));

                return (
                    <div className="modal-overlay">
                        <div className="modal-content invoice-modal">
                            <div className="modal-header no-print">
                                <div className="flex items-center gap-2">
                                    <FileText size={20} className="text-gold" />
                                    <h3>Document Viewer</h3>
                                </div>
                                <div className="flex gap-2">
                                    <button className="btn btn-secondary flex items-center gap-2" onClick={() => window.print()}>
                                        <Printer size={18} /> Print
                                    </button>
                                    <button className="close-btn" onClick={() => setSelectedInvoice(null)}><X size={20} /></button>
                                </div>
                            </div>

                            <div className="invoice-container printable-document">
                                <div className="invoice-header">
                                    <div className="brand-section">
                                        <div className="brand-logo-wrapper">
                                            <img src={logo} alt="Logo" className="brand-logo-circle" />
                                        </div>
                                        <div className="company-info">
                                            <h2 className="brand-name">SAFETY DRIVING SCHOOL</h2>
                                            <p className="admin-name">Nyasha (Admin)</p>
                                            <p className="contact">+263 772 123 456</p>
                                            <p className="address">123 Driving School Lane, Harare</p>
                                        </div>
                                    </div>
                                    <div className="document-type-section text-right">
                                        <h1 className="document-type-title">{selectedInvoice.type === 'Income' ? 'RECEIPT' : 'INVOICE'}</h1>
                                        <p className="document-ref font-bold">#INV-{new Date(selectedInvoice.payment_date || selectedInvoice.date).getFullYear()}-{selectedInvoice.id?.split('_')[1] || '001'}</p>
                                        <p className="document-date">Date: {new Date(selectedInvoice.payment_date || selectedInvoice.date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                    </div>
                                </div>

                                <div className="invoice-divider"></div>

                                <div className="invoice-meta-grid">
                                    <div className="bill-to-section">
                                        <h4 className="section-label">BILL TO:</h4>
                                        <h3 className="client-name">{customer.name || selectedInvoice.customerName}</h3>
                                        <p className="client-id">Student ID: {customer.studentId || 'N/A'}</p>
                                        <p className="client-id">ID Number: {customer.idNumber || 'N/A'}</p>
                                        <p className="client-package">Package: {pkg?.name || 'Custom Package'}</p>
                                        <p className="client-installment">Installment Type: {getInstallmentName(selectedInvoice.installmentType)}</p>
                                    </div>
                                    <div className="nok-section text-right">
                                        <h4 className="section-label">NEXT OF KIN:</h4>
                                        <h3 className="nok-name">{customer.nextOfKin?.name || '-'}</h3>
                                        <p className="nok-relation">Relationship: {customer.nextOfKin?.relationship || '-'}</p>
                                        <p className="nok-phone">Phone: {customer.nextOfKin?.phone || '-'}</p>
                                    </div>
                                </div>

                                <div className="invoice-table-container">
                                    <div className="table-header">
                                        <span>DESCRIPTION</span>
                                        <span className="text-right">AMOUNT</span>
                                    </div>
                                    <div className="table-row">
                                        <div className="flex flex-col">
                                            <span className="font-bold">{pkg?.name || 'Driver Training'} Course Enrolment</span>
                                            <span className="text-sm text-gray-500">Stage: {customer.stage || 'N/A'}</span>
                                        </div>
                                        <span className="text-right font-bold">${customer.totalAmount?.toFixed(2) || '0.00'}</span>
                                    </div>
                                    <div className="table-row subdued">
                                        <span className="italic">Payment Received ({new Date(selectedInvoice.payment_date || selectedInvoice.date).toLocaleString()}) - {selectedInvoice.method}</span>
                                        <span className="text-right text-red">-${selectedInvoice.amount.toFixed(2)}</span>
                                    </div>
                                    <div className="balance-info-row">
                                        <div className="total-label-group">
                                            <span className="label">CURRENT BALANCE DUE</span>
                                            <span className="date-subtext">As of {new Date().toLocaleDateString()}</span>
                                        </div>
                                        <div className="total-row">
                                            <span className="total-amount">${(customer.balance || 0).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoice-footer-grid">
                                    <div className="banking-details">
                                        <h4 className="section-label">BANKING DETAILS:</h4>
                                        <div className="bank-info-card">
                                            <p>Bank: <span className="font-bold">CABS Bank</span></p>
                                            <p>Acc No: <span className="font-bold">100 234 5678</span></p>
                                            <p>Branch: <span className="font-bold">First Street</span></p>
                                        </div>
                                    </div>
                                    <div className="aging-analysis text-right">
                                        <h4 className="section-label">AGING ANALYSIS:</h4>
                                        <table className="aging-table">
                                            <thead>
                                                <tr>
                                                    <th>Current</th>
                                                    <th>30 Days</th>
                                                    <th>60 Days</th>
                                                    <th>90+</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className="font-bold">${(customer.balance || 0).toFixed(2)}</td>
                                                    <td>-</td>
                                                    <td>-</td>
                                                    <td>-</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <RecordPaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                paymentToEdit={paymentToEdit}
            />

            {/* Import Payments Modal */}
            <ImportModal
                isOpen={isPaymentImportModalOpen}
                onClose={() => setIsPaymentImportModalOpen(false)}
                onImport={importPayments}
                title="Import Payments"
                templateHeaders={paymentTemplateHeaders}
                sampleRow={paymentSampleRow}
            />
        </div>
    );
};

export default Financials;
